import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { useFreshRef } from 'src/lib/hooks';
import { captureMessage } from 'src/lib/sentry';

import { CommonPopup } from './views/CommonPopup';
import { PopupContext } from './context';
import { closeAllPopups, closePopup, openPopup, usePopupReducer } from './reducer';

const propTypes = {
  onPopupStackChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export const Popups = ({ onPopupStackChange, children }) => {
  const registeredPopupsMapRef = useRef(new Map());
  const [state, dispatch] = usePopupReducer();
  const stateRef = useFreshRef(state);

  useEffect(() => {
    if (onPopupStackChange) {
      onPopupStackChange(state?.stack);
    }
  }, [onPopupStackChange, state?.stack]);

  const providerValue = useMemo(
    () => ({
      registerPopup: (popupName, popupComponent) => {
        registeredPopupsMapRef.current.set(popupName, popupComponent);
      },

      openPopup: (popupName, popupProps) => {
        checkPopupRegistered(registeredPopupsMapRef.current, popupName);
        dispatch(openPopup(popupName, popupProps));
      },

      closePopup: (popupName) => {
        checkPopupRegistered(registeredPopupsMapRef.current, popupName);
        dispatch(closePopup(popupName));
      },

      closeAllPopups: () => {
        dispatch(closeAllPopups());
      },

      isPopupVisible: (popupName) => {
        return stateRef.current.stack.includes(popupName);
      },
    }),
    [dispatch, stateRef]
  );

  return (
    <PopupContext.Provider value={providerValue}>
      {children}

      <CommonPopup
        registeredPopupsMap={registeredPopupsMapRef.current}
        activePopupsMap={state.activePopupsMap}
        stack={state.stack}
      />
    </PopupContext.Provider>
  );
};

const checkPopupRegistered = (registeredPopupsMap, popupName) => {
  if (registeredPopupsMap.has(popupName)) {
    return;
  }

  captureMessage(`Popups: Unknown popup type '${String(popupName)}'`, {
    level: 'warning',
  });
};

Popups.displayName = 'Popups';
Popups.propTypes = propTypes;
